import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Stack,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
} from '@mui/material';

interface ScheduleSelectorProps {
  value: string; // cron expression
  onChange: (cronExpression: string) => void;
}

type ScheduleType = 'once' | 'minutely' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

const ScheduleSelector: React.FC<ScheduleSelectorProps> = ({ value, onChange }) => {
  const [scheduleType, setScheduleType] = useState<ScheduleType>('daily');

  // Minutely
  const [everyNMinutes, setEveryNMinutes] = useState<number>(15);

  // Hourly
  const [everyNHours, setEveryNHours] = useState<number>(1);
  const [hourMinute, setHourMinute] = useState<number>(0);

  // Daily
  const [everyNDays, setEveryNDays] = useState<number>(1);
  const [dailyTime, setDailyTime] = useState<string>('00:00');

  // Weekly
  const [selectedDays, setSelectedDays] = useState<string[]>(['1']); // Default Monday
  const [weeklyTime, setWeeklyTime] = useState<string>('00:00');

  // Monthly
  const [monthlyType, setMonthlyType] = useState<'day' | 'weekday'>('day');
  const [dayOfMonth, setDayOfMonth] = useState<number>(1);
  const [monthlyTime, setMonthlyTime] = useState<string>('00:00');

  // Custom
  const [customCron, setCustomCron] = useState<string>('0 0 * * *');

  // Parse existing cron expression on mount or value change
  useEffect(() => {
    if (!value) return;

    const parts = value.trim().split(' ');
    if (parts.length !== 5) {
      setScheduleType('custom');
      setCustomCron(value);
      return;
    }

    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

    // Daily at specific time
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && minute !== '*' && hour !== '*') {
      setScheduleType('daily');
      setDailyTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
      return;
    }

    // Weekly on specific days
    if (dayOfMonth === '*' && month === '*' && dayOfWeek !== '*' && minute !== '*' && hour !== '*') {
      setScheduleType('weekly');
      const days = dayOfWeek.split(',');
      setSelectedDays(days);
      setWeeklyTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
      return;
    }

    // Monthly on specific day
    if (dayOfMonth !== '*' && month === '*' && dayOfWeek === '*' && minute !== '*' && hour !== '*') {
      setScheduleType('monthly');
      setMonthlyType('day');
      setDayOfMonth(parseInt(dayOfMonth));
      setMonthlyTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`);
      return;
    }

    // Hourly
    if (dayOfMonth === '*' && month === '*' && dayOfWeek === '*' && hour === '*' && minute !== '*') {
      setScheduleType('hourly');
      setHourMinute(parseInt(minute));
      return;
    }

    // Minutely
    if (minute.startsWith('*/') && hour === '*' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
      setScheduleType('minutely');
      setEveryNMinutes(parseInt(minute.replace('*/', '')));
      return;
    }

    // Default to custom
    setScheduleType('custom');
    setCustomCron(value);
  }, [value]);

  // Generate cron expression based on current settings
  useEffect(() => {
    let cron = '';

    switch (scheduleType) {
      case 'minutely':
        cron = `*/${everyNMinutes} * * * *`;
        break;

      case 'hourly':
        cron = `${hourMinute} */${everyNHours} * * *`;
        break;

      case 'daily':
        const [dHour, dMinute] = dailyTime.split(':');
        cron = `${parseInt(dMinute)} ${parseInt(dHour)} * * *`;
        break;

      case 'weekly':
        const [wHour, wMinute] = weeklyTime.split(':');
        const days = selectedDays.length > 0 ? selectedDays.sort().join(',') : '*';
        cron = `${parseInt(wMinute)} ${parseInt(wHour)} * * ${days}`;
        break;

      case 'monthly':
        if (monthlyType === 'day') {
          const [mHour, mMinute] = monthlyTime.split(':');
          cron = `${parseInt(mMinute)} ${parseInt(mHour)} ${dayOfMonth} * *`;
        } else {
          cron = customCron;
        }
        break;

      case 'custom':
        cron = customCron;
        break;

      default:
        cron = '0 0 * * *';
    }

    if (cron && cron !== value) {
      onChange(cron);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleType, everyNMinutes, everyNHours, hourMinute, everyNDays, dailyTime, selectedDays, weeklyTime, monthlyType, dayOfMonth, monthlyTime, customCron]);

  const handleDayToggle = (day: string) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort()
    );
  };

  return (
    <Box>
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>Schedule Type</InputLabel>
        <Select
          value={scheduleType}
          onChange={(e) => setScheduleType(e.target.value as ScheduleType)}
          label="Schedule Type"
        >
          <MenuItem value="minutely">Every N Minutes</MenuItem>
          <MenuItem value="hourly">Every N Hours</MenuItem>
          <MenuItem value="daily">Daily</MenuItem>
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="custom">Custom (Cron Expression)</MenuItem>
        </Select>
      </FormControl>

      {scheduleType === 'minutely' && (
        <Stack spacing={2}>
          <TextField
            label="Every N Minutes"
            type="number"
            size="small"
            fullWidth
            value={everyNMinutes}
            onChange={(e) => setEveryNMinutes(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 59 }}
            helperText="Run every N minutes (1-59)"
          />
        </Stack>
      )}

      {scheduleType === 'hourly' && (
        <Stack spacing={2}>
          <TextField
            label="Every N Hours"
            type="number"
            size="small"
            fullWidth
            value={everyNHours}
            onChange={(e) => setEveryNHours(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 23 }}
            helperText="Run every N hours (1-23)"
          />
          <TextField
            label="At Minute"
            type="number"
            size="small"
            fullWidth
            value={hourMinute}
            onChange={(e) => setHourMinute(Math.max(0, Math.min(59, parseInt(e.target.value) || 0)))}
            inputProps={{ min: 0, max: 59 }}
            helperText="Run at this minute of the hour (0-59)"
          />
        </Stack>
      )}

      {scheduleType === 'daily' && (
        <Stack spacing={2}>
          <TextField
            label="Every N Days"
            type="number"
            size="small"
            fullWidth
            value={everyNDays}
            onChange={(e) => setEveryNDays(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 365 }}
            helperText="Run every N days"
          />
          <TextField
            label="Time"
            type="time"
            size="small"
            fullWidth
            value={dailyTime}
            onChange={(e) => setDailyTime(e.target.value)}
            helperText="Time to run (24-hour format)"
          />
        </Stack>
      )}

      {scheduleType === 'weekly' && (
        <Stack spacing={2}>
          <Box>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#666' }}>
              Select Days of Week:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DAYS_OF_WEEK.map(day => (
                <Chip
                  key={day.value}
                  label={day.label}
                  onClick={() => handleDayToggle(day.value)}
                  color={selectedDays.includes(day.value) ? 'primary' : 'default'}
                  variant={selectedDays.includes(day.value) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </Box>
          <TextField
            label="Time"
            type="time"
            size="small"
            fullWidth
            value={weeklyTime}
            onChange={(e) => setWeeklyTime(e.target.value)}
            helperText="Time to run on selected days"
          />
        </Stack>
      )}

      {scheduleType === 'monthly' && (
        <Stack spacing={2}>
          <RadioGroup
            value={monthlyType}
            onChange={(e) => setMonthlyType(e.target.value as 'day' | 'weekday')}
          >
            <FormControlLabel
              value="day"
              control={<Radio size="small" />}
              label="On a specific day of the month"
            />
          </RadioGroup>
          {monthlyType === 'day' && (
            <TextField
              label="Day of Month"
              type="number"
              size="small"
              fullWidth
              value={dayOfMonth}
              onChange={(e) => setDayOfMonth(Math.max(1, Math.min(31, parseInt(e.target.value) || 1)))}
              inputProps={{ min: 1, max: 31 }}
              helperText="Day of the month (1-31)"
            />
          )}
          <TextField
            label="Time"
            type="time"
            size="small"
            fullWidth
            value={monthlyTime}
            onChange={(e) => setMonthlyTime(e.target.value)}
            helperText="Time to run"
          />
        </Stack>
      )}

      {scheduleType === 'custom' && (
        <TextField
          label="Cron Expression"
          size="small"
          fullWidth
          value={customCron}
          onChange={(e) => setCustomCron(e.target.value)}
          helperText="Format: minute hour dayOfMonth month dayOfWeek (e.g., '0 0 * * *')"
        />
      )}

      <Box sx={{ mt: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          Cron Expression: <strong>{value}</strong>
        </Typography>
      </Box>
    </Box>
  );
};

export default ScheduleSelector;
